import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  TypedUseSelectorHook,
  useDispatch as useReduxDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';
import type { Middleware } from 'redux';
import {
  createTransform,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';

const REMEMBER_ME_KEY = 'rememberMe';

// use localStorage when rememberMe is true, sessionStorage otherwise
const hybridStorage = {
  getItem: (key: string) =>
    Promise.resolve(sessionStorage.getItem(key) ?? localStorage.getItem(key)),
  setItem: (key: string, value: string) => {
    sessionStorage.setItem(key, value);
    if (localStorage.getItem(REMEMBER_ME_KEY) === 'true') {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
    return Promise.resolve();
  },
  removeItem: (key: string) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
    return Promise.resolve();
  },
};

import { authApi, authSlice, AuthState } from 'src/services/auth';
import { collectionApi, collectionSlice } from 'src/services/collection';
import { config } from 'src/services/config';
import { firmApi } from 'src/services/firm';
import { preferencesSlice } from 'src/services/preferences';
import { userApi, userSlice } from 'src/services/user';
import { videoApi, videoSlice } from 'src/services/video';
import { statisticsApi } from '../statistics/api';

const rootReducer = combineReducers({
  [authApi.reducerPath]: authApi.reducer,
  [authSlice.name]: authSlice.reducer,
  [collectionApi.reducerPath]: collectionApi.reducer,
  [collectionSlice.name]: collectionSlice.reducer,
  [firmApi.reducerPath]: firmApi.reducer,
  [preferencesSlice.name]: preferencesSlice.reducer,
  [statisticsApi.reducerPath]: statisticsApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [userSlice.name]: userSlice.reducer,
  [videoApi.reducerPath]: videoApi.reducer,
  [videoSlice.name]: videoSlice.reducer,
});

const authTransform = createTransform(
  (state: AuthState) => {
    localStorage.setItem(REMEMBER_ME_KEY, String(!!state.rememberMe));
    return { ...state };
  },
  (state) => {
    return { ...state, isAuthorized: false };
  },
  { whitelist: [authSlice.name] },
);

const logoutListener: Middleware = (api) => (next) => (action) => {
  const result = next(action);
  if (authApi.endpoints.logOut.matchPending(action) || (action as { type?: string }).type === 'auth/logout') {
    api.dispatch(authApi.util.resetApiState());
    api.dispatch(collectionApi.util.resetApiState());
    api.dispatch(firmApi.util.resetApiState());
    api.dispatch(statisticsApi.util.resetApiState());
    api.dispatch(userApi.util.resetApiState());
    api.dispatch(videoApi.util.resetApiState());
  }
  return result;
};

const persistConfig = {
  key: 'root',
  storage: hybridStorage,
  transforms: [authTransform],
  version: 1,
  whitelist: [authSlice.name, preferencesSlice.name],
};

const persistedReducer = persistReducer(
  persistConfig,
  rootReducer,
) as typeof rootReducer;

export const store = configureStore({
  devTools: config.enableReduxDevTools,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(
      logoutListener,
      authApi.middleware,
      collectionApi.middleware,
      firmApi.middleware,
      statisticsApi.middleware,
      userApi.middleware,
      videoApi.middleware,
    ),
  reducer: persistedReducer,
});

export const persistor = persistStore(store);
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export const useDispatch = () => useReduxDispatch<AppDispatch>();
export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;
